/**
 * Enrollment state machine.
 *
 * Owns the lifecycle of a single enrollment attempt:
 *   idle → submitting → (complete | partial | polling | error | …)
 *
 * Independence requirements (so this is portable to a future public/anonymous
 * page wrapper without refactor):
 *   - Does not import from redux. Toast surfacing is handled by the page.
 *   - Accepts an injectable `apiCaller` so the public version can swap in
 *     a different auth path (HMAC token, captcha-protected proxy) without
 *     changing the inner UI.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import bbApi from "../../api"
import { EnrollApiBody, EnrollApiResult } from "../../api/enroll"
import {
  EnrollmentResponse,
  EnrollRequest,
  GenericError,
  ValidationError
} from "./types"

/**
 * Discriminated union — easier to render against than a soup of booleans.
 * Each kind maps to one render branch in the page.
 */
export type EnrollState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "polling"; enrollment: EnrollmentResponse }
  | { kind: "complete"; enrollment: EnrollmentResponse }
  | { kind: "partial"; enrollment: EnrollmentResponse }
  | {
      kind: "retry_blocked"
      enrollment_id: number
      message: string
      enrollment?: EnrollmentResponse
    }
  | {
      kind: "validation_error"
      message: string
      fields: ValidationError["fields"]
    }
  | {
      kind: "idempotency_conflict"
      enrollment_id: number
      message: string
    }
  | { kind: "error"; message: string }

export interface EnrollApiCaller {
  start: (payload: EnrollRequest) => Promise<EnrollApiResult>
  get: (id: number) => Promise<EnrollApiResult>
  retry: (id: number) => Promise<EnrollApiResult>
}

export interface UseEnrollmentOptions {
  /** Defaults to bbApi.enroll. Override for the public/anonymous variant. */
  apiCaller?: EnrollApiCaller
  /** Called whenever an idempotency_conflict happens. Use for toast surfacing. */
  onIdempotencyConflict?: (message: string) => void
  /** Called for any error state (network, 4xx/5xx other than partial/validation). */
  onError?: (message: string) => void
}

export interface UseEnrollmentReturn {
  state: EnrollState
  /** Start a fresh enrollment with form values. */
  start: (
    values: Omit<EnrollRequest, "idempotency_key">
  ) => Promise<EnrollState>
  /** Retry the current enrollment (replays stored input on the server). */
  retry: () => Promise<EnrollState>
  /** Reset to idle and rotate the idempotency key. */
  reset: () => void
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

const generateKey = (): string => {
  // crypto.randomUUID is widely supported in modern browsers; fall back if missing
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `enroll-${crypto.randomUUID()}`
  }
  return `enroll-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const isEnrollmentResponse = (
  body: EnrollApiBody | null
): body is EnrollmentResponse =>
  !!body && "enrollment_id" in body && "steps" in body

const isValidationError = (
  body: EnrollApiBody | null
): body is ValidationError =>
  !!body &&
  (body as ValidationError).status === "error" &&
  (body as ValidationError).error === "validation"

const isGenericError = (body: EnrollApiBody | null): body is GenericError => {
  if (!body || !("status" in body) || body.status !== "error") return false
  // ValidationError also has status:"error" — exclude it by checking the error code
  const code = (body as ValidationError | GenericError).error
  return code !== "validation"
}

const fallbackMessage = (result: EnrollApiResult): string => {
  if (result.networkError) return result.networkError
  if (result.body && "message" in result.body && (result.body as any).message) {
    return (result.body as any).message
  }
  if (result.status === 401) return "Authentication required."
  if (result.status === 403)
    return "You do not have permission to perform this enrollment."
  if (result.status === 404) return "Enrollment not found."
  if (result.status === 0)
    return "Network request failed. Check your connection."
  return `Enrollment failed (HTTP ${result.status}).`
}

export const useEnrollment = (
  options: UseEnrollmentOptions = {}
): UseEnrollmentReturn => {
  const apiCaller = options.apiCaller ?? bbApi.enroll
  const [state, setState] = useState<EnrollState>({ kind: "idle" })

  // useRef so re-renders don't churn the key, and so unmount cleanup is reliable
  const idempotencyKeyRef = useRef<string>(generateKey())
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollDeadlineRef = useRef<number>(0)
  const isMountedRef = useRef(true)

  const cancelPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      cancelPoll()
    }
  }, [cancelPoll])

  /**
   * Translate an HTTP response into a state. Used by start/retry/poll —
   * the response shape is identical for all three entry points.
   */
  const resultToState = useCallback((result: EnrollApiResult): EnrollState => {
    const { status, body } = result

    // 200 — complete
    if (status === 200 && isEnrollmentResponse(body)) {
      return { kind: "complete", enrollment: body }
    }

    // 207 — partial / failed steps
    if (status === 207 && isEnrollmentResponse(body)) {
      return { kind: "partial", enrollment: body }
    }

    // 202 — still in progress (rare; usually only on replay)
    if (status === 202 && isEnrollmentResponse(body)) {
      return { kind: "polling", enrollment: body }
    }

    // 400 — validation
    if (status === 400 && isValidationError(body)) {
      return {
        kind: "validation_error",
        message: body.message,
        fields: body.fields ?? {}
      }
    }

    // 409 — idempotency conflict
    if (status === 409 && isGenericError(body)) {
      return {
        kind: "idempotency_conflict",
        enrollment_id: body.enrollment_id ?? 0,
        message: body.message
      }
    }

    // 429 — retry limit exceeded
    if (status === 429 && isGenericError(body)) {
      return {
        kind: "retry_blocked",
        enrollment_id: body.enrollment_id ?? 0,
        message: body.message
      }
    }

    // Anything else (404, 401, 403, 500, network) → generic error
    return { kind: "error", message: fallbackMessage(result) }
  }, [])

  /**
   * Schedule the next poll tick. Called from `pollOnce` and from the initial
   * 202 transition.
   */
  const scheduleNextPoll = useCallback(
    (enrollmentId: number) => {
      if (!isMountedRef.current) return
      pollTimerRef.current = setTimeout(async () => {
        if (!isMountedRef.current) return

        if (Date.now() >= pollDeadlineRef.current) {
          cancelPoll()
          setState({
            kind: "error",
            message:
              "Enrollment is still running after 5 minutes. Check the server logs or query the enrollment by id directly."
          })
          return
        }

        const result = await apiCaller.get(enrollmentId)
        if (!isMountedRef.current) return

        const next = resultToState(result)
        setState(next)
        if (next.kind === "polling") {
          scheduleNextPoll(enrollmentId)
        } else {
          cancelPoll()
        }
      }, POLL_INTERVAL_MS)
    },
    [apiCaller, cancelPoll, resultToState]
  )

  const applyTerminal = useCallback(
    (next: EnrollState): EnrollState => {
      if (!isMountedRef.current) return next

      // Side effects on transitions out of submit/retry
      if (next.kind === "idempotency_conflict") {
        // Auto-rotate the key so the next attempt is clean
        idempotencyKeyRef.current = generateKey()
        options.onIdempotencyConflict?.(next.message)
      }
      if (next.kind === "error") {
        options.onError?.(next.message)
      }

      setState(next)

      if (next.kind === "polling") {
        pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS
        scheduleNextPoll(next.enrollment.enrollment_id)
      }
      return next
    },
    [options, scheduleNextPoll]
  )

  const start = useCallback(
    async (
      values: Omit<EnrollRequest, "idempotency_key">
    ): Promise<EnrollState> => {
      cancelPoll()
      setState({ kind: "submitting" })

      const payload: EnrollRequest = {
        ...values,
        idempotency_key: idempotencyKeyRef.current
      }
      const result = await apiCaller.start(payload)
      if (!isMountedRef.current) return { kind: "idle" }

      return applyTerminal(resultToState(result))
    },
    [apiCaller, applyTerminal, cancelPoll, resultToState]
  )

  const retry = useCallback(async (): Promise<EnrollState> => {
    // We can retry from `partial`, `error` (if we have an id), or `polling`
    const enrollmentId =
      "enrollment" in state
        ? state.enrollment?.enrollment_id
        : "enrollment_id" in state
        ? state.enrollment_id
        : null

    if (!enrollmentId) {
      const next: EnrollState = {
        kind: "error",
        message: "Cannot retry: no enrollment id available."
      }
      options.onError?.(next.message)
      setState(next)
      return next
    }

    cancelPoll()
    setState({ kind: "submitting" })

    const result = await apiCaller.retry(enrollmentId)
    if (!isMountedRef.current) return { kind: "idle" }

    return applyTerminal(resultToState(result))
  }, [apiCaller, applyTerminal, cancelPoll, options, resultToState, state])

  const reset = useCallback(() => {
    cancelPoll()
    idempotencyKeyRef.current = generateKey()
    setState({ kind: "idle" })
  }, [cancelPoll])

  return { state, start, retry, reset }
}

/**
 * Helper used by enroll-form.tsx to map server validation errors
 * (dot notation: "first_user.email") into Formik's nested error shape.
 */
export const mapFieldErrorsToFormik = (
  fields: ValidationError["fields"]
): Record<string, any> => {
  const out: Record<string, any> = {}
  for (const [path, info] of Object.entries(fields)) {
    const segments = path.split(".")
    let cursor = out
    for (let i = 0; i < segments.length - 1; i++) {
      const key = segments[i]
      if (typeof cursor[key] !== "object" || cursor[key] === null) {
        cursor[key] = {}
      }
      cursor = cursor[key]
    }
    cursor[segments[segments.length - 1]] = info.message
  }
  return out
}
